import type {
  Memo,
  CreateMemoDto,
  UpdateMemoDto,
} from "@repo/exit-calc-interfaces";
import type { StateStatus, StoreDeps } from "../types";
import { createStore } from "../helpers/create-store";
import { withFetchStatus } from "../helpers/with-fetch-status";

interface MemoState {
  memos: Memo[];
  status: StateStatus;
}

interface MemoActions {
  fetch: () => Promise<void>;
  create: (data: CreateMemoDto) => Promise<Memo | null>;
  update: (id: number, data: UpdateMemoDto) => Promise<Memo | null>;
  remove: (id: number) => Promise<boolean>;
}

export type MemoStore = MemoState & MemoActions;

export function createMemoStore(deps: StoreDeps) {
  return createStore<MemoStore>("MemoStore", (set, get) => ({
    memos: [],
    status: "idle",

    fetch: async () => {
      const setStatus = (status: StateStatus) =>
        set((state) => {
          state.status = status;
        });
      await withFetchStatus(setStatus, deps.logger, async () => {
        const memos = await deps.api.memo.list();
        set((state) => {
          state.memos = memos;
        });
      });
    },

    create: async (data) => {
      try {
        const memo = await deps.api.memo.create(data);
        set((state) => {
          state.memos.unshift(memo);
        });
        return memo;
      } catch (error) {
        deps.logger.error(error);
        return null;
      }
    },

    update: async (id, data) => {
      try {
        const memo = await deps.api.memo.update(id, data);
        set((state) => {
          const idx = state.memos.findIndex((m) => m.id === id);
          if (idx >= 0) state.memos[idx] = memo;
        });
        return memo;
      } catch (error) {
        deps.logger.error(error);
        return null;
      }
    },

    remove: async (id) => {
      try {
        await deps.api.memo.remove(id);
        set((state) => {
          state.memos = state.memos.filter((m) => m.id !== id);
        });
        return true;
      } catch (error) {
        deps.logger.error(error);
        return false;
      }
    },
  }));
}
